/**
 * What a session replay may carry about the people using the app.
 *
 * rrweb masks input values while it records; the upload-time pass below is the backstop that
 * keeps a recording from ever leaving the browser with typed text in it, whatever the recorder
 * was configured with.
 */

type Attributes = Record<string, string | number | boolean | null>;

interface SerializedElement {
  type: typeof ELEMENT;
  tagName: string;
  attributes: Attributes;
  childNodes: SerializedNode[];
  id: number;
}

interface SerializedOther {
  type: number;
  childNodes?: SerializedNode[];
  id: number;
}

type SerializedNode = SerializedElement | SerializedOther;

interface Addition {
  parentId: number;
  nextId: number | null;
  node: SerializedNode;
}

interface AttributeChange {
  id: number;
  attributes: Attributes;
}

interface MutationData {
  source: number;
  adds?: Addition[];
  attributes?: AttributeChange[];
}

interface InputData {
  source: number;
  id: number;
  text: string;
  isChecked: boolean;
}

interface RecordedEvent {
  type: number;
  data?: unknown;
}

const FULL_SNAPSHOT = 2;
const INCREMENTAL_SNAPSHOT = 3;
const MUTATION = 0;
const INPUT = 5;
const ELEMENT = 2;
const VALUE_BEARING = new Set(['input', 'textarea', 'select']);

/** The elements a recording has shown so far, so a later attribute change can be placed. */
export interface RecordedElements {
  /** ids of inputs, textareas and selects */
  inputs: Set<number>;
  /** ids of every element */
  all: Set<number>;
}

/** @returns an empty {@link RecordedElements} */
export function recordedElements(): RecordedElements {
  return { inputs: new Set(), all: new Set() };
}

/**
 * The privacy half of rrweb's `record()` options. Every input value is masked: rrweb replaces the
 * text with asterisks of the same length, and no `maskInputFn` is passed because one that returns
 * the text for any field switches that masking off.
 *
 * @param maskAllText whether every text node is masked as well, not only those marked `pii`,
 *   `sensitive` or `replay-mask`
 * @returns options to spread into `record()`
 */
export function replayPrivacyOptions(maskAllText = false) {
  return {
    maskAllInputs: true,
    maskTextClass: /\b(?:pii|sensitive|replay-mask)\b/,
    ...(maskAllText ? { maskTextSelector: 'body' } : {}),
    // Removed from the snapshot entirely: secrets, admin-only widgets, iframed third parties.
    blockClass: /\b(?:replay-block|no-replay)\b/,
    // A canvas can hold a signature or a chart of someone's finances.
    recordCanvas: false,
  };
}

function masked(text: string) {
  return '*'.repeat(text.length);
}

function isElement(node: SerializedNode): node is SerializedElement {
  return node.type === ELEMENT;
}

/**
 * Replaces every recorded input value with asterisks of the same length, in place: the `value`
 * attribute of inputs, textareas and selects in snapshots and added nodes, later `value`
 * attribute changes on those elements, and the text of input events. A `value` change on an
 * element the recording has not shown yet (an on-error buffer starts mid-document) is masked
 * as well, since it cannot be told from an input. Checked states are kept, since a checkbox
 * reveals no more than its label.
 *
 * @param events rrweb events of one chunk, oldest first; chunks of a recording are passed in
 *   order so the elements a later chunk's attribute changes refer to are known
 * @param seen the elements shown so far, updated as snapshots and additions are read
 * @returns the same array
 */
export function maskRecordedInputs(events: unknown[], seen: RecordedElements = recordedElements()) {
  const visit = (node: SerializedNode) => {
    if (isElement(node)) {
      seen.all.add(node.id);
      if (VALUE_BEARING.has(node.tagName)) {
        seen.inputs.add(node.id);
        maskValue(node.attributes);
      }
    }
    for (const child of node.childNodes ?? []) visit(child);
  };

  for (const event of events as RecordedEvent[]) {
    if (event.type === FULL_SNAPSHOT) {
      const root = (event.data as { node?: SerializedNode } | undefined)?.node;
      if (root) {
        seen.inputs.clear();
        seen.all.clear();
        visit(root);
      }
      continue;
    }
    if (event.type !== INCREMENTAL_SNAPSHOT) continue;
    const data = event.data as MutationData | InputData | undefined;
    if (data?.source === MUTATION) {
      const mutation = data as MutationData;
      for (const add of mutation.adds ?? []) visit(add.node);
      for (const change of mutation.attributes ?? []) {
        if (seen.inputs.has(change.id) || !seen.all.has(change.id)) maskValue(change.attributes);
      }
    } else if (data?.source === INPUT) {
      const input = data as InputData;
      if (typeof input.text === 'string') input.text = masked(input.text);
    }
  }
  return events;
}

function maskValue(attributes: Attributes) {
  const value = attributes['value'];
  if (typeof value === 'string' && value.length > 0) attributes['value'] = masked(value);
}
